/* eslint-disable @typescript-eslint/no-explicit-any */
// The HR Assistant's controls over the final documents, available at any time once
// everything is ready (before AND after the release):
//   • who signs — the name and designation printed under the Relieving Order, the
//     Service Certificate and the other letters;
//   • the office order number (after the release);
//   • regenerating the documents with the new details.
import React, { useState } from "react";
import { toast } from "react-toastify";
import { EmployeeSearchInput } from "./components";
import { updateFinalDocs } from "../services/eSeparation.service";

type SigningAuthority = {
  empId?: string | null;
  name: string;
  label: string;
  is_custom: boolean;
};

const FinalDocsManager: React.FC<{
  requestId: string;
  signing: SigningAuthority | null;
  released: boolean;
  officeOrderNo?: string | null;
  confirm: (message: string, opts?: any) => Promise<boolean>;
  onChanged: (patch: Record<string, any>) => void;
  onRegenerate: (docType: "service_certificate" | "relieving_letter", docName: string) => void;
}> = ({ requestId, signing, released, officeOrderNo, confirm, onChanged, onRegenerate }) => {
  const [query, setQuery] = useState("");
  const [empId, setEmpId] = useState("");
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [orderNo, setOrderNo] = useState(officeOrderNo || "");
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);

  const saveSigning = async () => {
    // With an employee selected, a blank designation is filled in from their record.
    if (!name.trim() || (!designation.trim() && !empId.trim()))
      return toast.warning("Enter the name and the designation of the signing authority.");
    if (
      !(await confirm(
        `Set ${name.trim()}${designation.trim() ? `, ${designation.trim()}` : ""} as the signing authority? The Relieving Order, Service Certificate and the other letters will show this name and designation when downloaded.`,
        { title: "Change Signing Authority", confirmLabel: "Save", tone: "primary" }
      ))
    )
      return;
    setBusy(true);
    const res = await updateFinalDocs({
      request_id: requestId,
      signatory_emp_id: empId.trim() || undefined,
      signatory_name: name.trim(),
      signatory_designation: designation.trim() || undefined,
    });
    setBusy(false);
    if (res.success) {
      toast.success("Signing authority updated. Regenerate the documents to apply it.");
      setChanged(true);
      onChanged({
        signatory_emp_id: empId.trim() || null,
        signatory_name: name.trim(),
        signatory_designation: designation.trim() || null,
        // The designation may have been filled in on the server; shown after a reload.
        signing_authority: { empId: empId.trim() || null, name: name.trim(), label: designation.trim() || "(from the employee's record)", is_custom: true },
      });
      setQuery("");
      setEmpId("");
      setName("");
      setDesignation("");
    } else toast.error(res.message || "Failed to update the signing authority");
  };

  const resetSigning = async () => {
    setBusy(true);
    const res = await updateFinalDocs({ request_id: requestId, clear_signatory: true });
    setBusy(false);
    if (res.success) {
      toast.success("Signing authority reset to the configured HOD HR. Regenerate the documents to apply it.");
      setChanged(true);
      onChanged({
        signatory_emp_id: null,
        signatory_name: null,
        signatory_designation: null,
        signing_authority: signing ? { ...signing, is_custom: false } : null,
      });
    } else toast.error(res.message || "Failed to reset the signing authority");
  };

  const saveOrderNo = async () => {
    const no = orderNo.trim();
    if (!no) return toast.warning("Enter the office order number.");
    if (!/^[A-Za-z0-9][A-Za-z0-9\-/]{0,29}$/.test(no))
      return toast.warning('Office order number may contain only letters, digits, "-" and "/" (max 30 characters).');
    if (no === officeOrderNo) return toast.info("The office order number is unchanged.");
    setBusy(true);
    const res = await updateFinalDocs({ request_id: requestId, office_order_no: no });
    setBusy(false);
    if (res.success) {
      toast.success("Office order number updated. Regenerate the documents to apply it.");
      setChanged(true);
      onChanged({ office_order_no: no });
    } else toast.error(res.message || "Failed to update the office order number");
  };

  return (
    <div className="rounded p-3 mb-3" style={{ background: "#F6F8FB", border: "1px solid rgba(0,0,0,0.08)" }}>
      <div className="fw-semibold mb-1">Signing authority &amp; office order</div>
      <div className="small text-muted mb-3">
        You can change these at any time — even after the documents were generated — and regenerate them.
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold text-secondary small mb-1">Documents are signed by</label>
        <div className="fw-semibold">
          {signing?.name || "—"}
          <span className="text-muted fw-normal">
            {signing?.label ? `, ${signing.label}` : ""}
            {signing?.empId ? ` (${signing.empId})` : ""}
          </span>
          <span
            className="ms-2 badge"
            style={{ background: signing?.is_custom ? "#FFF1DE" : "#EEEDEB", color: signing?.is_custom ? "#BA7517" : "#78746D" }}
          >
            {signing?.is_custom ? "Changed" : "Configured HOD HR"}
          </span>
        </div>
      </div>

      <div className="row g-2 align-items-end mb-2">
        <div className="col-lg-4">
          <label className="form-label fw-semibold text-secondary small mb-1">Employee ID</label>
          <EmployeeSearchInput
            value={query}
            onChange={(v) => {
              setQuery(v);
              setEmpId("");
            }}
            onSelect={(item) => {
              setQuery(item.emp_no);
              setEmpId(item.emp_no);
              setName(item.emp_name || "");
              // A search result can carry the designation CODE (digits) instead of the
              // title — never put that on a document; leave blank to auto-fill the title.
              setDesignation(/^\d+$/.test(String(item.designation || "").trim()) ? "" : item.designation || "");
            }}
            placeholder="Search employee number or name…"
          />
        </div>
        <div className="col-lg-3">
          <label className="form-label fw-semibold text-secondary small mb-1">Name</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name to print" />
        </div>
        <div className="col-lg-3">
          <label className="form-label fw-semibold text-secondary small mb-1">Designation</label>
          <input
            className="form-control"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            placeholder={empId ? "Auto-filled from the employee's record" : "e.g. Manager/HR/O&M"}
          />
        </div>
        <div className="col-lg-2 d-flex gap-2">
          <button className="btn btn-primary flex-grow-1" disabled={busy} onClick={saveSigning}>
            Save
          </button>
        </div>
      </div>
      {signing?.is_custom && (
        <button className="btn btn-sm btn-link p-0 mb-2" disabled={busy} onClick={resetSigning}>
          Reset to the configured HOD HR
        </button>
      )}

      {released && (
        <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
          <span className="fw-semibold small">Office order number</span>
          <input
            className="form-control"
            style={{ maxWidth: 220 }}
            maxLength={30}
            value={orderNo}
            onChange={(e) => setOrderNo(e.target.value)}
          />
          <button className="btn btn-outline-primary" disabled={busy} onClick={saveOrderNo}>
            Update
          </button>
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
        <span className="fw-semibold small me-1">Regenerate</span>
        <button
          className={`btn ${changed ? "btn-success" : "btn-outline-success"}`}
          disabled={!released}
          onClick={() => onRegenerate("service_certificate", "Service Certificate")}
        >
          <i className="ti ti-refresh me-1" />
          Service Certificate
        </button>
        <button
          className={`btn ${changed ? "btn-success" : "btn-outline-success"}`}
          disabled={!released}
          onClick={() => onRegenerate("relieving_letter", "Relieving Order")}
        >
          <i className="ti ti-refresh me-1" />
          Relieving Order
        </button>
        {!released && <small className="text-muted">Available once released.</small>}
      </div>
    </div>
  );
};

export default FinalDocsManager;
